import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import DashboardStatsAndBarChart from './components/BarChartCard';
import CategoryDonutChart from './components/DonutChartCard';
import RecentlyAdded from './components/RecentlyAddedList';
import UsersPanel from './components/UsersList';
import { dashboardService, DashboardResponse, TrendData } from '../../services/dashboardService';
import DashboardSkeleton from './components/DashboardSkeleton';
import { getLogoBySubdomainOptimized } from '../../utils/logoUtils';

const Dashboard = () => {
    const isMobile = useMediaQuery('(max-width:600px)');
    
    // State management
    const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [barLoading, setBarLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [barError, setBarError] = useState<string | null>(null);
    const [selectedPeriod, setSelectedPeriod] = useState('6m');
    const [barTrend, setBarTrend] = useState<TrendData[]>([]);
    const [barCounts, setBarCounts] = useState<any>(null);
    const [selectedStatus, setSelectedStatus] = useState<'all' | 'open' | 'approved' | 'rejected'>('all');

    // Fetch dashboard data
    const fetchDashboardData = useCallback(async (period: string = '6m') => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await dashboardService.getDashboardStats({
                period,
                trend_type: 'months'
            });
            
            setDashboardData(response);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
            console.error('Error fetching dashboard data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBarGraphData = useCallback(async () => {
        try {
            setBarLoading(true);
            setBarError(null);

            const response = await dashboardService.getBarGraph({
                status_filter: selectedStatus,
                trend_type: 'months'
            });

            setBarTrend(response.trend);
            setBarCounts(response.counts);
        } catch (err) {
            setBarError(err instanceof Error ? err.message : 'Failed to fetch bar graph data');
            console.error('Error fetching bar graph data:', err);
        } finally {
            setBarLoading(false);
        }
    }, [selectedStatus]);

    // Load dashboard data on component mount and when period changes
    useEffect(() => {
        fetchDashboardData(selectedPeriod);
    }, [fetchDashboardData, selectedPeriod]);

    // Load bar graph data on mount and when status changes
    useEffect(() => {
        fetchBarGraphData();
    }, [fetchBarGraphData]);

    // Handle period change
    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period);
    };

    // Show loading state only for initial dashboard data load
    if (loading) {
        return <DashboardSkeleton />;
    }

    // Show error state
    if (error || barError) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="100vh"
                px={isMobile ? 0 : 3}
            >
                <Typography color="error" variant="h6">
                    {error || barError}
                </Typography>
            </Box>
        );
    }

    // Prepare safe fallbacks to render UI even if some fields are missing
    const safeSummary = dashboardData?.summary ?? { total: 0, open: 0, approved: 0, rejected: 0 };
    const safeCategory = dashboardData?.category_breakdown ?? { electricity: 0, waste: 0, water: 0, waste_water: 0 };
    const safeIndigents = dashboardData?.recent_indigents ?? [];
    const safeUsers = dashboardData?.recent_users ?? [];

    return (
    <Box px={isMobile?0:3} sx={{  minHeight: '100vh' }}>
      {/* Header */}
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} mb={3} spacing={isMobile ? 2 : 0}>
        <Stack direction="row" spacing={4} alignItems="center">
          <Box component="img" src={getLogoBySubdomainOptimized()} alt="Company Logo" sx={{ height: 70 }} />
          <Box>
            <Typography variant="h6" fontWeight={600} fontSize="22px" color="#132F5F" fontFamily="Poppins, sans-serif">
              Good Day,
            </Typography>
            <Typography variant="body2" color="#6b7280">
              Track, review, and resolve complaints efficiently. Use the insights <br /> below to monitor trends and stay on top of pending actions.
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Stats & Charts */}
      <Stack 
        direction={{ xs: 'column', lg: 'row' }} 
        spacing={{ xs: 4, lg: 4 }} 
        mb={4}
      >
        <Box 
          flex={{ lg: 2 }} 
          display="flex" 
          flexDirection="column" 
          gap={4}
        >
          <Box sx={{ minHeight: { lg: '480px' } }}>
              <DashboardStatsAndBarChart 
                summary={safeSummary}
                trend={barTrend}
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
                selectedStatus={selectedStatus}
                onStatusChange={setSelectedStatus}
                counts={barCounts}
                isLoading={barLoading}
              />
          </Box>
          <Box>
            <RecentlyAdded indigents={safeIndigents} />
          </Box>
        </Box>
        <Box 
          flex={{ lg: 1 }} 
          display="flex" 
          flexDirection="column" 
          gap={6}
        >
          <Box>
            <CategoryDonutChart categoryData={safeCategory} />
          </Box>
          <Box sx={{ mt: { xs: 2, lg: 1.5 } }}>
            <UsersPanel users={safeUsers} />
          </Box>
        </Box>
      </Stack>

      {/* Recently Added */}
      
    </Box>
  );
};

export default Dashboard;

